import React, { useState } from "react";
import { Space, Button, Row, Col, notification, Typography } from "antd";

import type { ColumnsType } from "antd/es/table";
import {
  LoadingOutlined,
  EditOutlined,
  EyeOutlined,
  PrinterOutlined,
  CloudDownloadOutlined,
  ShareAltOutlined,
  SendOutlined
} from "@ant-design/icons";

import { useGetVisitsQuery } from "../../../redux/services/visitApi";
import TopScrollTable from "../../../components/TopScrollTable";
import Filters from "../Filters";
import { Link, useNavigate } from "react-router-dom";
import {
  getStatusText,
  localizeDateTime,
  getQuerySearchParams,
  applyPaginationToQueryParams
} from "../../../utils";
import { BASE_URL } from "../../../redux/services/baseApi";
import { useAppSelector } from "../../../redux/hooks";

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}
const { Text } = Typography;

const Visit = () => {
  const querySearchParams = getQuerySearchParams();
  const [queryParams, setQueryParams] = useState<any>({
    include_all: true,
    ecb_code: parseInt(querySearchParams.ecb_code),
    page: parseInt(querySearchParams.page) || 1,
    page_size: parseInt(querySearchParams.page_size) || 20
  });
  const navigate = useNavigate();

  const auth = useAppSelector<any>((store) => store.auth);

  const [api, contextHolder] = notification.useNotification();
  const [downloadInProgress, setDownloadInProgress] = useState<any>({});

  const { data, isLoading, isFetching } = useGetVisitsQuery({
    ...queryParams,
    include_all: true
  });
  const visits = data?.visits || [];

  const user = auth.user;
  const isAdminUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user", "management"].includes(role.role_type)
  );
  const isZonalManager = user?.roles?.some((role: { role_type: string }) =>
    ["zonal_manager"].includes(role.role_type)
  );

  const isClient = user?.user_type === "client";

  const columns: ColumnsType<DataType> = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name"
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number"
    },
    {
      title: "Visit Date",
      dataIndex: "scheduled_visit_date_time",
      key: "scheduled_visit_date_time",
      render: (scheduled_visit_date_time) => (
        <Text>{scheduled_visit_date_time ? localizeDateTime(scheduled_visit_date_time) : "-"}</Text>
      )
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      render: (brand) => <>{brand?.name}</>
    },
    {
      title: "Visit Code",
      dataIndex: "ecb_visit_code",
      key: "ecb_visit_code"
    },
    {
      title: "EV City",
      dataIndex: "city",
      key: "city",
      render: (city) => <>{city?.name}</>
    },
    {
      title: "City Manager",
      dataIndex: "assigned_city_manager",
      key: "assigned_city_manager",
      render: (assigned_city_manager) => <>{assigned_city_manager?.name || "-"}</>
    },
    {
      title: "Partner",
      dataIndex: "visit_partner",
      key: "visit_partner",
      render: (visit_partner) => <>{visit_partner?.name || "-"}</>
    },

    {
      title: "Field Agent",
      dataIndex: "assigned_field_agent1",
      key: "assigned_field_agent1",
      render: (assigned_field_agent1) => <>{assigned_field_agent1?.name || "-"}</>
    },
    {
      title: "Visit Status",
      dataIndex: "status",
      key: "status",
      render: (_, visit: any) => <>{getStatusText(visit)}</>
    },
    {
      title: "Customer Rating",
      dataIndex: "customer_rating",
      key: "customer_rating",
      render: (_, visit: any) => (
        <>
          {visit.customer_rating && visit?.status === "completed"
            ? `${visit.customer_rating}/5`
            : "-"}
        </>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, visit: any) => (
        <Space size="middle">
          {isClient &&
            (visit?.status == "completed" ||
              visit?.status == "scheduled" ||
              visit?.status == "in_progress") ? (
            <>
              <Link to={`${visit?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
              {visit?.status !== "cancelled" && (
                <Link to={`/${visit?.id}/share-visit`} target="_blank">
                  <Button icon={<ShareAltOutlined />} title="Share"></Button>
                </Link>
              )}
            </>
          ) : (
            <>
              <Link to={`${visit?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
              {((visit?.status === "completed" && (isZonalManager || isAdminUser)) ||
                visit?.status !== "completed") && (
                  <Link to={`${visit?.id}/update`}>
                    <Button icon={<EditOutlined />} title="Update"></Button>
                  </Link>
                )}
              {visit?.status !== "cancelled" && (
                <>
                  {isAdminUser && (
                    <Link to={`/${visit?.id}/print-visit`} target="_blank">
                      <Button icon={<PrinterOutlined />} title="Print"></Button>
                    </Link>
                  )}
                  <Link to={`/${visit?.id}/share-visit`} target="_blank">
                    <Button icon={<ShareAltOutlined />} title="Share"></Button>
                  </Link>
                  <Link to={`/${visit?.id}/customer-report-visit`} target="_blank">
                    <Button icon={<SendOutlined />} title="Share"></Button>
                  </Link>
                </>
              )}
              {visit?.status === "completed" && (
                <>
                  {visit?.zip_file ? (
                    <Link to={visit?.zip_file} target="_blank">
                      <Button title="Download Zip">
                        <CloudDownloadOutlined />
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      disabled={
                        Object.prototype.hasOwnProperty.call(downloadInProgress, visit?.id) &&
                        downloadInProgress[visit?.id] === true
                      }
                      title="Download Zip"
                      onClick={() => handleDownload(visit?.id, visit?.ecb_visit_code)}
                    >
                      {Object.prototype.hasOwnProperty.call(downloadInProgress, visit?.id) &&
                        downloadInProgress[visit?.id] === true ? (
                        <LoadingOutlined style={{ fontSize: 12 }} spin />
                      ) : (
                        <CloudDownloadOutlined />
                      )}
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </Space>
      )
    }
  ];

  const clientColumns: ColumnsType<DataType> = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name"
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number"
    },
    {
      title: "Visit Date",
      dataIndex: "scheduled_visit_date_time",
      key: "scheduled_visit_date_time",
      render: (scheduled_visit_date_time) => (
        <Text>{scheduled_visit_date_time ? localizeDateTime(scheduled_visit_date_time) : "-"}</Text>
      )
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      render: (brand) => <>{brand?.name}</>
    },
    {
      title: "Visit Code",
      dataIndex: "ecb_visit_code",
      key: "ecb_visit_code"
    },
    {
      title: "EV City",
      dataIndex: "city",
      key: "city",
      render: (city) => <>{city?.name}</>
    },
    {
      title: "Visit Status",
      dataIndex: "status",
      key: "status",
      render: (_, visit: any) => <>{getStatusText(visit)}</>
    },
    {
      title: "Action",
      key: "action",
      render: (_, visit: any) => (
        <Space size="middle">
          {isClient &&
            (visit?.status == "completed" ||
              visit?.status == "scheduled" ||
              visit?.status == "in_progress") ? (
            <>
              <Link to={`${visit?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
              {visit?.status === "completed" && (
                <Link to={`/${visit?.id}/share-visit`} target="_blank">
                  <Button icon={<ShareAltOutlined />} title="Share"></Button>
                </Link>
              )}
            </>
          ) : (
            <>
              <Link to={`${visit?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
              {((visit?.status === "completed" && isAdminUser) ||
                visit?.status !== "completed") && (
                  <Link to={`${visit?.id}/update`}>
                    <Button icon={<EditOutlined />} title="Update"></Button>
                  </Link>
                )}
              {visit?.status !== "cancelled" && (
                <>
                  {isAdminUser && (
                    <Link to={`/${visit?.id}/print-visit`} target="_blank">
                      <Button icon={<PrinterOutlined />} title="Print"></Button>
                    </Link>
                  )}
                  <Link to={`/${visit?.id}/share-visit`} target="_blank">
                    <Button icon={<ShareAltOutlined />} title="Share"></Button>
                  </Link>
                  <Link to={`/${visit?.id}/customer-report-visit`} target="_blank">
                    <Button icon={<SendOutlined />} title="Share"></Button>
                  </Link>
                </>
              )}
              {visit?.status === "completed" && (
                <>
                  {visit?.zip_file ? (
                    <Link to={visit?.zip_file} target="_blank">
                      <Button title="Download Zip">
                        <CloudDownloadOutlined />
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      disabled={
                        Object.prototype.hasOwnProperty.call(downloadInProgress, visit?.id) &&
                        downloadInProgress[visit?.id] === true
                      }
                      title="Download Zip"
                      onClick={() => handleDownload(visit?.id, visit?.ecb_visit_code)}
                    >
                      {Object.prototype.hasOwnProperty.call(downloadInProgress, visit?.id) &&
                        downloadInProgress[visit?.id] === true ? (
                        <LoadingOutlined style={{ fontSize: 12 }} spin />
                      ) : (
                        <CloudDownloadOutlined />
                      )}
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </Space>
      )
    }
  ];

  const pagination = {
    pageSize: parseInt(querySearchParams.page_size) || 20,
    current: parseInt(querySearchParams.page) || 1,
    total: data?.meta?.total_count,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
    onChange: (page: number, pageSize: number) => {
      const values = {
        page,
        page_size: pageSize
      };

      setQueryParams((old: any) => ({ ...old, ...values }));

      applyPaginationToQueryParams({
        ...queryParams,
        ...values,
        navigate
      });
    }
  };

  const handleDownload = async (id: number, filename: string) => {
    try {
      setDownloadInProgress((prev: any) => ({
        ...prev,
        [id]: true
      }));
      const response = await fetch(`${BASE_URL}assignments/${id}/download_zip`, {
        method: "GET",
        headers: {
          "access-token": auth?.accessToken,
          "token-type": "Bearer",
          client: auth.client,
          uid: auth.uid
        }
      });

      if (!response.ok) {
        setDownloadInProgress((prev: any) => ({
          ...prev,
          [id]: false
        }));
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const blob = await response.blob();

      // Create a blob URL for the downloaded data
      const downloadUrl = URL.createObjectURL(blob);

      // Create a link and click it to initiate download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${filename}.zip`;
      link.click();
      setDownloadInProgress((prev: any) => ({
        ...prev,
        [id]: false
      }));
    } catch (error) {
      console.error("Error downloading ZIP:", error);
      setDownloadInProgress((prev: any) => ({
        ...prev,
        [id]: false
      }));
    }
  };

  return (
    <>
      {contextHolder}
      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <div>
            <h2>Visits</h2>
            <Text>Total visits: {data?.meta?.total_count || 0}</Text>
          </div>
        </Col>
      </Row>

      <Filters
        applyFilters={(values: any) =>
          setQueryParams((oldValues: any) => ({ ...oldValues, ...values }))
        }
        visits
      />
      <TopScrollTable
        rowKey="id"
        columns={isClient ? clientColumns : columns}
        dataSource={visits}
        bordered
        loading={isLoading || isFetching}
        scroll={{ x: 400 }}
        pagination={pagination}
      />
    </>
  );
};

export default Visit;
